import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { api } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import type { Message, Room } from "ghost-server";

type Action = "delete" | "delete-author" | "ban";
type ConfirmKey = `${Action}:${string}`;

export function MessagesSection() {
	const [searchParams, setSearchParams] = useSearchParams();
	const roomId = searchParams.get("roomId");

	const [messages, setMessages] = useState<Message[]>([]);
	const [rooms, setRooms] = useState<Room[]>([]);
	const [confirmKey, setConfirmKey] = useState<ConfirmKey | null>(null);

	async function load() {
		const [msgRes, roomRes] = await Promise.all([api.admin.messages.all.get(), api.admin.rooms.all.get()]);
		if (msgRes.data) setMessages(msgRes.data);
		if (roomRes.data) setRooms(roomRes.data);
	}

	useEffect(() => { load(); }, []);

	const filteredMessages = useMemo(
		() => (roomId ? messages.filter((m) => m.roomId === roomId) : messages),
		[messages, roomId],
	);

	const filteredRoom = roomId ? rooms.find((r) => r.id === roomId) : null;

	function clearFilter() {
		const next = new URLSearchParams(searchParams);
		next.delete("roomId");
		setSearchParams(next);
	}

	async function handleDelete(id: string) {
		await api.admin.messages.delete.delete({ id });
		setConfirmKey(null);
		load();
	}

	async function handleDeleteByAuthor(authorId: string) {
		await api.admin.messages["delete-by-author"].delete({ authorId });
		setConfirmKey(null);
		load();
	}

	async function handleBanIp(ip: string) {
		await api.admin.messages["ban-ip"].post({ ip });
		setConfirmKey(null);
		load();
	}

	return (
		<div className="space-y-4">
			<div className="flex items-center gap-3">
				<p className="text-xs text-[#333] font-mono">{filteredMessages.length} messages</p>
				{roomId && (
					<button
						onClick={clearFilter}
						className="flex items-center gap-2 px-2 py-1 text-[10px] font-mono uppercase tracking-widest border border-[#00ffcc33] text-[#00ffcc] hover:bg-[#00ffcc08] cursor-pointer"
					>
						<span>filtered to room {filteredRoom?.name ?? roomId}</span>
						<span aria-hidden>×</span>
					</button>
				)}
			</div>

			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Content</TableHead>
						<TableHead>Author</TableHead>
						<TableHead>Room</TableHead>
						<TableHead>IP</TableHead>
						<TableHead>Created</TableHead>
						<TableHead className="w-56" />
					</TableRow>
				</TableHeader>
				<TableBody>
					{filteredMessages.map((msg) => {
						const deleteKey: ConfirmKey = `delete:${msg.id}`;
						const authorKey: ConfirmKey = `delete-author:${msg.id}`;
						const banKey: ConfirmKey = `ban:${msg.id}`;
						const isConfirming = confirmKey?.endsWith(`:${msg.id}`);

						return (
							<TableRow key={msg.id}>
								<TableCell className="max-w-[240px] truncate text-[#00ffcc]">{msg.content}</TableCell>
								<TableCell className="font-mono text-xs">{msg.authorId}</TableCell>
								<TableCell className="font-mono text-xs">{msg.roomId}</TableCell>
								<TableCell className="font-mono text-xs">{msg.ip ?? "—"}</TableCell>
								<TableCell>{new Date(msg.createdAt).toLocaleString()}</TableCell>
								<TableCell>
									{confirmKey === deleteKey ? (
										<div className="flex gap-1 justify-end">
											<Button size="sm" variant="destructive" onClick={() => handleDelete(msg.id)}>confirm delete</Button>
											<Button size="sm" variant="ghost" onClick={() => setConfirmKey(null)}>cancel</Button>
										</div>
									) : confirmKey === authorKey ? (
										<div className="flex gap-1 justify-end">
											<Button size="sm" variant="destructive" onClick={() => handleDeleteByAuthor(msg.authorId)}>
												delete all from {msg.authorId.slice(0, 6)}…
											</Button>
											<Button size="sm" variant="ghost" onClick={() => setConfirmKey(null)}>cancel</Button>
										</div>
									) : confirmKey === banKey ? (
										<div className="flex gap-1 justify-end">
											<Button size="sm" variant="destructive" onClick={() => msg.ip && handleBanIp(msg.ip)}>
												ban {msg.ip}
											</Button>
											<Button size="sm" variant="ghost" onClick={() => setConfirmKey(null)}>cancel</Button>
										</div>
									) : !isConfirming ? (
										<div className="flex gap-1 justify-end">
											<Button size="sm" variant="ghost" onClick={() => setConfirmKey(deleteKey)}>delete</Button>
											<Button size="sm" variant="ghost" onClick={() => setConfirmKey(authorKey)}>del-user</Button>
											<Button
												size="sm"
												variant="ghost"
												disabled={!msg.ip}
												onClick={() => setConfirmKey(banKey)}
											>
												ban
											</Button>
										</div>
									) : null}
								</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		</div>
	);
}
